import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useLessonPlayer } from "@/hooks/useLessonPlayer";
import { useCanEditCourse } from "@/hooks/useCanEditCourse";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { LoadingAnimation } from "@/components/LoadingAnimation";
import { LessonEditor } from "@/components/courses/LessonEditor";
import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Circle, 
  Play, 
  FileText,
  ArrowLeft,
  Pencil
} from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Lesson = Database["public"]["Tables"]["lessons"]["Row"];

const CoursePlayer = () => {
  const { slug, lessonId } = useParams<{ slug: string; lessonId?: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [courseId, setCourseId] = useState<string>("");
  const [editMode, setEditMode] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  
  const { canEdit } = useCanEditCourse(courseId);

  // Fetch course ID on mount
  useEffect(() => {
    const fetchCourseId = async () => {
      const { data } = await supabase
        .from("courses")
        .select("id")
        .eq("slug", slug)
        .single();
      
      if (data) {
        setCourseId(data.id);
      }
    };
    if (slug) {
      fetchCourseId();
    }
  }, [slug]);
  
  const {
    modules,
    currentLesson,
    progress,
    loading,
    error,
    isEnrolled,
    selectLesson,
    markLessonComplete,
    getNextLesson,
    getPreviousLesson,
    getCurrentLessonIndex,
    getTotalLessons,
  } = useLessonPlayer(courseId, lessonId);

  const handleMarkComplete = async () => {
    if (!currentLesson) return;
    
    const result = await markLessonComplete(currentLesson.id);
    if (result.success) {
      toast.success(t("coursePlayer.completedLesson"));
      
      // Auto-advance to next lesson
      const nextLesson = getNextLesson();
      if (nextLesson) {
        navigate(`/courses/${slug}/learn/${nextLesson.id}`);
      }
    }
  };

  const handleLessonClick = (lessonId: string) => {
    navigate(`/courses/${slug}/learn/${lessonId}`);
  };

  const handleNext = () => {
    const nextLesson = getNextLesson();
    if (nextLesson) {
      navigate(`/courses/${slug}/learn/${nextLesson.id}`);
    }
  };

  const handlePrevious = () => {
    const previousLesson = getPreviousLesson();
    if (previousLesson) {
      navigate(`/courses/${slug}/learn/${previousLesson.id}`);
    }
  };

  const handleLessonSave = (updatedLesson: Lesson) => {
    // The useLessonPlayer hook doesn't expose a method to update a lesson locally,
    // so we'll refresh the page to show updated content
    window.location.reload();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t("coursePlayer.notEnrolled")}</h1>
          <Button onClick={() => navigate("/auth")}>Login</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingAnimation size="lg" />
      </div>
    );
  }

  if (!isEnrolled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t("coursePlayer.notEnrolled")}</h1>
          <Button onClick={() => navigate(`/courses/${slug}`)}>
            {t("coursePlayer.enrollNow")}
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t("coursePlayer.errorLoading")}</h1>
          <Button onClick={() => navigate(`/courses/${slug}`)}>
            {t("coursePlayer.backToCourse")}
          </Button>
        </div>
      </div>
    );
  }

  const currentLessonProgress = currentLesson ? progress[currentLesson.id] : null;
  const isCompleted = currentLessonProgress?.completed || false;
  const totalLessons = getTotalLessons();
  const currentIndex = getCurrentLessonIndex();
  const completedCount = Object.values(progress).filter(p => p.completed).length;
  const progressPercent = totalLessons > 0 ? (completedCount / totalLessons) * 100 : 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${slug}`)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("coursePlayer.backToCourse")}
          </Button>
          
          <div className="flex items-center gap-4">
            {canEdit && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border">
                <Pencil className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t("courseEditor.editMode")}</span>
                <Switch
                  checked={editMode}
                  onCheckedChange={setEditMode}
                />
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              {t("coursePlayer.lesson")} {currentIndex} {t("coursePlayer.of")} {totalLessons}
            </div>
            <div className="w-32">
              <Progress value={progressPercent} />
            </div>
            <span className="text-sm font-medium">{Math.round(progressPercent)}%</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            {currentLesson ? (
              <>
                {/* Video/Content Area */}
                {currentLesson.lesson_type === "video" && currentLesson.video_url ? (
                  <AspectRatio ratio={16 / 9} className="bg-muted rounded-lg overflow-hidden mb-6">
                    <iframe
                      src={currentLesson.video_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </AspectRatio>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg flex items-center justify-center mb-6">
                    <FileText className="h-20 w-20 text-primary/40" />
                  </div>
                )}

                {/* Lesson Info */}
                <div className="mb-8">
                  <div className="flex items-start justify-between mb-4">
                    <h1 className="text-3xl font-bold">{currentLesson.title}</h1>
                    {editMode && canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingLesson(currentLesson as Lesson)}
                        className="gap-2 shrink-0"
                      >
                        <Pencil className="h-4 w-4" />
                        {t("courseEditor.editLesson")}
                      </Button>
                    )}
                  </div>
                  {currentLesson.description && (
                    <p className="text-muted-foreground mb-4">{currentLesson.description}</p>
                  )}
                  
                  {currentLesson.content && (
                    <div className="prose prose-invert max-w-none mb-6">
                      <div dangerouslySetInnerHTML={{ __html: currentLesson.content }} />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mb-8">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={!getPreviousLesson()}
                    className="gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t("coursePlayer.previousLesson")}
                  </Button>

                  {!isCompleted && (
                    <Button onClick={handleMarkComplete} className="gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      {t("coursePlayer.markComplete")}
                    </Button>
                  )}

                  <Button
                    onClick={handleNext}
                    disabled={!getNextLesson()}
                    className="gap-2"
                  >
                    {t("coursePlayer.nextLesson")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-muted-foreground">{t("coursePlayer.noContent")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Course Content */}
        <aside className="w-80 border-l border-border bg-muted/30 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold">{t("coursePlayer.courseContent")}</h2>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {modules.map((module) => (
                <div key={module.id}>
                  <h3 className="font-medium mb-2 text-sm">{module.title}</h3>
                  <div className="space-y-1">
                    {module.lessons.map((lesson) => {
                      const lessonProgress = progress[lesson.id];
                      const isCurrentLesson = currentLesson?.id === lesson.id;
                      
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => handleLessonClick(lesson.id)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            isCurrentLesson
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {lessonProgress?.completed ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : isCurrentLesson ? (
                                <Play className="h-4 w-4" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${
                                !isCurrentLesson && "text-muted-foreground"
                              }`}>
                                {lesson.title}
                              </p>
                              {lesson.duration_minutes && (
                                <p className={`text-xs ${
                                  isCurrentLesson ? "text-primary-foreground/70" : "text-muted-foreground"
                                }`}>
                                  {lesson.duration_minutes} min
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <Separator className="mt-4" />
                </div>
              ))}
            </div>
          </ScrollArea>
        </aside>
      </div>

      {/* Lesson Editor Dialog */}
      {editingLesson && (
        <LessonEditor
          lesson={editingLesson}
          isOpen={!!editingLesson}
          onClose={() => setEditingLesson(null)}
          onSave={handleLessonSave}
        />
      )}
    </div>
  );
};

export default CoursePlayer;
